export interface ComposableJob {
  id: string;
  route: string;
  titleKey: string;
  descriptionKey: string;
  needles: string[];
}

const SUPPORT_JOB_IDS = new Set(['job-intents', 'job-tasks']);

const STOPWORDS = new Set([
  'و', 'از', 'به', 'با', 'برای', 'براش', 'که', 'این', 'یک', 'رو', 'تو', 'توی', 'را', 'تا', 'هم',
  'کنم', 'کنیم', 'بشه', 'شود', 'میخوام', 'میخواهم', 'میخوایم', 'ثبت', 'ایجاد', 'جدید', 'فلان',
  'بفرستم', 'بساز', 'بسازیم', 'کن', 'the', 'a', 'an', 'and', 'or', 'for', 'with', 'to', 'of',
  'in', 'on', 'new', 'create', 'want', 'make', 'then', 'after', 'در', 'نهایت'
]);

const GENERIC_SLUGS = new Set([
  'new', 'edit', 'list', 'id', 'admin', 'hub', 'core', 'page', 'app', 'api', 'marketing',
  'management', 'universal', 'module', 'index', 'detail', 'view', 'form', 'ai', 'web',
  'assist', 'the', 'and'
]);

const QUERY_ALIASES: Array<{ from: string; to: string[] }> = [
  { from: 'هوش مصنوعی', to: ['ai', 'assist'] },
  { from: 'بریف', to: ['brief'] },
  { from: 'استودیو محتوا', to: ['ace'] },
  { from: 'استودیوی محتوا', to: ['ace'] },
  { from: 'مقاله', to: ['article', 'blog', 'publishing'] },
  { from: 'مقالات', to: ['article', 'blog', 'publishing'] },
  { from: 'وبسایت', to: ['website', 'blog', 'storefront'] },
  { from: 'سایت', to: ['website', 'blog', 'storefront'] },
  { from: 'محتوا', to: ['content'] },
  { from: 'انتشار', to: ['publishing', 'publish'] },
  { from: 'پیش فاکتور', to: ['proforma', 'quote'] },
  { from: 'پیشفاکتور', to: ['proforma', 'quote'] },
  { from: 'فاکتور', to: ['invoice'] },
  { from: 'لید', to: ['lead'] },
  { from: 'سرنخ', to: ['lead'] },
  { from: 'پرداخت', to: ['payment', 'receipt'] },
  { from: 'کاربر', to: ['user'] },
  { from: 'نقش', to: ['role'] },
  { from: 'سفارش', to: ['order'] },
  { from: 'کمپین', to: ['campaign'] },
  { from: 'بیمه', to: ['insurance', 'policy'] },
  { from: 'گردش کار', to: ['workflow'] },
  { from: 'گردشکار', to: ['workflow'] },
  { from: 'اعلان', to: ['notification'] },
  { from: 'انبار', to: ['inventory', 'warehouse'] },
  { from: 'طرف حساب', to: ['party', 'customer'] },
  { from: 'فرمان کار', to: ['intent', 'workflow'] },
  { from: 'نیت', to: ['intent'] }
];

export function normalize(value: string): string {
  return (value ?? '')
    .toLowerCase()
    .replaceAll('\u200c', '')
    .replaceAll('ي', 'ی')
    .replaceAll('ك', 'ک')
    .replace(/[^\p{L}\p{N}\s]+/gu, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

export function uniqueNeedles(values: string[]): string[] {
  const seen = new Set<string>();
  const needles: string[] = [];
  for (const value of values) {
    const needle = normalize(value);
    if (needle.length < 2 || seen.has(needle) || GENERIC_SLUGS.has(needle)) {
      continue;
    }

    seen.add(needle);
    needles.push(needle);
  }

  return needles.sort((a, b) => b.length - a.length || a.localeCompare(b));
}

export function slugTokens(route: string): string[] {
  return (route ?? '')
    .split('/')
    .flatMap(segment => segment.split('-'))
    .map(token => normalize(token))
    .filter(token => token.length > 2 && !GENERIC_SLUGS.has(token));
}

export function camelTokens(value: string): string[] {
  const last = (value ?? '').split('.').pop() ?? '';
  return uniqueNeedles(last.replace(/([a-z])([A-Z])/g, '$1 $2').split(/[\s_-]+/));
}

export function composeJobPath(query: string, jobs: ComposableJob[]): ComposableJob[] {
  const original = normalize(query);
  if (!original) {
    return [];
  }

  const aliasHits = collectAliasHits(original);
  const matches: Array<{ job: ComposableJob; index: number; length: number; curated: boolean }> = [];
  for (const job of jobs) {
    const curated = isCurated(job);
    for (const needle of job.needles) {
      let from = 0;
      while (from < original.length) {
        const index = original.indexOf(needle, from);
        if (index < 0) {
          break;
        }

        matches.push({ job, index, length: needle.length, curated });
        from = index + Math.max(1, needle.length);
      }
    }

    const needleSet = new Set(job.needles);
    const wordSet = new Set(job.needles.flatMap(needleWords));
    for (const hit of aliasHits) {
      if (hit.tokens.some(token => needleSet.has(token) || wordSet.has(token))) {
        matches.push({ job, index: hit.index, length: hit.length, curated });
      }
    }
  }

  for (const token of original.split(' ').filter(item => item.length >= 3 && !STOPWORDS.has(item))) {
    let from = 0;
    while (from < original.length) {
      const index = original.indexOf(token, from);
      if (index < 0) {
        break;
      }

      for (const job of jobs) {
        if (job.needles.some(needle => needleWords(needle).includes(token))) {
          matches.push({ job, index, length: token.length, curated: isCurated(job) });
        }
      }

      from = index + token.length;
    }
  }

  matches.sort((a, b) => b.length - a.length || Number(b.curated) - Number(a.curated) || a.index - b.index);
  const claimed: Array<{ start: number; end: number; job: ComposableJob }> = [];
  const used = new Set<string>();
  for (const match of matches) {
    if (used.has(match.job.id) || used.has(match.job.route)) {
      continue;
    }

    const end = match.index + match.length;
    if (claimed.some(span => match.index < span.end && end > span.start)) {
      continue;
    }

    used.add(match.job.id);
    used.add(match.job.route);
    claimed.push({ start: match.index, end, job: match.job });
  }

  claimed.sort((a, b) => a.start - b.start);
  return disambiguate(claimed.map(span => span.job)).slice(0, 8);
}

export function pickCannedPlay<T extends { id?: string; steps: Array<{ route: string; optional?: boolean }> }>(
  composed: Array<{ route: string }>,
  plays: T[]
): T | null {
  const routes = composed.map(item => item.route);
  const exact = plays.find(play => sameRoutes(routes, play.steps.map(step => step.route)));
  if (exact) {
    return exact;
  }

  const composedSet = new Set(routes);
  const ranked = plays
    .map(play => {
      const requiredRoutes = play.steps.filter(step => !step.optional).map(step => step.route);
      const playRoutes = play.steps.map(step => step.route);
      const overlap = requiredRoutes.filter(route => composedSet.has(route)).length;
      const covered = routes.filter(route => playRoutes.includes(route)).length;
      const curated = !(play.id ?? '').startsWith('play-crawled-');
      return { play, overlap, covered, curated };
    })
    .filter(item => item.overlap >= 2)
    .sort((a, b) =>
      b.overlap - a.overlap
      || Number(b.curated) - Number(a.curated)
      || a.play.steps.length - b.play.steps.length
      || b.covered - a.covered
    );

  return ranked[0]?.play ?? null;
}

function significantTokens(value: string): string[] {
  return normalize(value).split(' ').filter(token => token.length > 1 && !STOPWORDS.has(token));
}

function containsPhrase(haystack: string, phrase: string): boolean {
  const hayTokens = normalize(haystack).split(' ').filter(Boolean);
  const phraseTokens = normalize(phrase).split(' ').filter(Boolean);
  if (phraseTokens.length === 0 || hayTokens.length < phraseTokens.length) {
    return false;
  }

  for (let index = 0; index <= hayTokens.length - phraseTokens.length; index += 1) {
    if (phraseTokens.every((token, offset) => hayTokens[index + offset] === token)) {
      return true;
    }
  }

  return false;
}

export function matchPublishedWorkflows(
  query: string,
  definitions: Array<{ id: string; name: string; description?: string; status?: string; isActive?: boolean }>
): Array<{ id: string; name: string; route: string; score: number }> {
  const original = normalize(query);
  if (!original) {
    return [];
  }

  const queryTokens = significantTokens(original);
  const queryTokenSet = new Set(queryTokens);
  return definitions
    .filter(item => item.isActive !== false && normalize(item.status ?? 'published') === 'published')
    .map(item => {
      const name = normalize(item.name);
      const haystack = `${name} ${normalize(item.description ?? '')}`.trim();
      const haystackTokens = new Set(significantTokens(haystack));
      let score = 0;
      if (containsPhrase(haystack, original) || (name.length >= 3 && containsPhrase(original, name))) {
        score = 100;
      } else {
        const matched = queryTokens.filter(token => haystackTokens.has(token)).length;
        const nameTokens = significantTokens(name);
        const nameCovered = nameTokens.length >= 2 && nameTokens.every(token => queryTokenSet.has(token));
        if (nameCovered) {
          score = 92;
        } else if (queryTokens.length > 0 && matched === queryTokens.length) {
          score = 80;
        } else if (queryTokens.length > 0 && matched >= 2 && matched / queryTokens.length >= 0.6) {
          score = 80;
        }
      }

      return {
        id: item.id,
        name: item.name,
        route: `/workflow/definitions/${item.id}`,
        score
      };
    })
    .filter(item => item.score >= 80)
    .sort((a, b) => b.score - a.score || a.name.length - b.name.length)
    .slice(0, 3);
}

function collectAliasHits(original: string): Array<{ index: number; length: number; tokens: string[] }> {
  const hits: Array<{ index: number; length: number; tokens: string[] }> = [];
  for (const alias of QUERY_ALIASES) {
    const from = normalize(alias.from);
    if (!from) {
      continue;
    }

    const tokens = alias.to.map(normalize).filter(token => token.length >= 2 && !GENERIC_SLUGS.has(token));
    if (tokens.length === 0) {
      continue;
    }

    let searchFrom = 0;
    while (searchFrom < original.length) {
      const index = original.indexOf(from, searchFrom);
      if (index < 0) {
        break;
      }

      hits.push({ index, length: from.length, tokens });
      searchFrom = index + Math.max(1, from.length);
    }
  }

  return hits;
}

function isCurated(job: ComposableJob): boolean {
  return !job.id.startsWith('nav:');
}

function needleWords(needle: string): string[] {
  return needle.split(' ').filter(word => word.length >= 3 && !STOPWORDS.has(word) && !GENERIC_SLUGS.has(word));
}

function sameRoutes(left: string[], right: string[]): boolean {
  return left.length === right.length && left.every((route, index) => route === right[index]);
}

function disambiguate(jobs: ComposableJob[]): ComposableJob[] {
  const ids = new Set(jobs.map(job => job.id));
  const routes = new Set(jobs.map(job => job.route));
  return jobs.filter(job => {
    if (jobs.length > 1 && SUPPORT_JOB_IDS.has(job.id)) {
      return false;
    }

    if ((job.id === 'job-parties' || job.route === '/platform/parties') && routes.has('/growth/crm/leads')) {
      return false;
    }

    if (job.id === 'job-journal' && (ids.has('job-invoices') || ids.has('job-quotes') || ids.has('job-receipts'))) {
      return false;
    }

    if (job.id === 'job-catalog-offerings' && ids.has('job-quotes')) {
      return false;
    }

    return true;
  });
}
